import * as React from "react";

import styles from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantTextContent.module.css";
import { AgentThreadResponseMarkdown } from "@/AgentThread/markdown";
import { AISummaryMarkdownLink, EntityLink } from "@/shared/components/ai";
import type { DirectEntityType } from "@/shared/components/RobotoMarkdown/entityRoutes";
import { RobotoTypography } from "@/shared/components/RobotoTypography";

interface AssistantTextContentProps {
  text: string;
  isGenerating: boolean;
  isCancelling?: boolean;
  onLinkClick?: () => void;
}

const AssistantTextContent = ({
  text,
  isGenerating,
  isCancelling = false,
  onLinkClick,
}: AssistantTextContentProps) => {
  const linkRenderers = React.useMemo(() => {
    const createLinkRenderer = <T extends Record<string, unknown>>(
      props: T & { onLinkClick?: () => void; text: string },
    ) => {
      return <AISummaryMarkdownLink {...props} />;
    };

    return {
      datasetLinkRenderer: (props: {
        datasetId: string;
        onLinkClick?: () => void;
        text: string;
      }) => createLinkRenderer(props),
      fileLinkRenderer: (props: {
        fileId: string;
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
      }) => createLinkRenderer(props),
      topicLinkRenderer: (props: {
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
        topicId: string;
      }) => createLinkRenderer(props),
      messagePathLinkRenderer: (props: {
        messagePathId: string;
        onLinkClick?: () => void;
        text: string;
        time?: bigint;
      }) => createLinkRenderer(props),
      entityLinkRenderer: (props: {
        entityType: DirectEntityType;
        entityId: string;
        onLinkClick?: () => void;
        text: string;
      }) => <EntityLink {...props} />,
    };
  }, []);

  return (
    <div className={styles.indentedContainer}>
      <div className={styles.messageContainer}>
        <AgentThreadResponseMarkdown
          content={text}
          {...linkRenderers}
          onLinkClick={onLinkClick}
        />
        {(isGenerating || isCancelling) && (
          <RobotoTypography className={styles.generatingText}>
            {isCancelling ? "Cancelling..." : "Generating..."}
          </RobotoTypography>
        )}
      </div>
    </div>
  );
};

export { AssistantTextContent };
